/**
 * StockIntakeRepository — the goods-received document.
 *
 *   GET    /stock/intakes                  list, newest first
 *   GET    /stock/intakes/{id}             one document
 *   POST   /stock/intakes                  create a draft
 *   PUT    /stock/intakes/{id}             edit a draft
 *   DELETE /stock/intakes/{id}             delete a draft
 *   POST   /stock/intakes/{id}/post        append purchase events, freeze
 *   POST   /stock/intakes/{id}/void        append reversals
 *   POST   /stock/intakes/receive          create and post in one step
 *   GET    /stock/intakes/last             the supplier's last delivery
 *   POST   /stock/intakes/{id}/attachments presigned upload, then confirm
 *   DELETE /stock/intakes/{id}/attachments/{attachmentId}   drafts only
 *
 * Draft, then post. A posted document is immutable, because the events it
 * wrote are already part of every terminal's arithmetic — correcting one
 * means voiding it and entering a new one. There is deliberately no endpoint
 * that edits a posted document's lines.
 *
 * Every total is computed by the SERVER. `estimateTotals` below exists only
 * so the editor can show a figure while the user types; what gets stored is
 * whatever the server returns. The dashboard never decides money or stock.
 *
 * See docs/feature-stock-intake-and-suppliers.md §5.1 and §7.1.
 */
import {posApiRequest} from './posApi';

export type IntakeStatus = 'draft' | 'posted' | 'void';
export type LineKind = 'product' | 'ingredient';

export interface IntakeLine {
  lineId: string;
  kind: LineKind;
  productId?: string | null;
  stockId?: string | null;
  /** Required for a product tracked per variant. */
  variantId?: string | null;
  /** Snapshotted by the server, like name. */
  variantLabel?: string | null;
  /** Snapshotted by the server from the master record at save time. */
  name?: string;
  unit?: string;
  qty: number;
  unitCost: number;
  lineTotal?: number;
  /** Returned by receive-now: what the line's target holds after posting. */
  onHandAfter?: number | null;
}

/** The supplier's paper, in a private bucket. `url` expires in 15 minutes. */
export interface IntakeAttachment {
  attachmentId: string;
  key: string;
  fileName: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  uploadedBy?: string;
  url?: string;
}

export type PostedVia = 'draft' | 'receive_now' | 'import';

/** Attachment limits, matching the server. */
export const ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
];
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS = 5;

export interface IntakeCharge {
  label: string;
  amount: number;
}

export interface IntakeDiscount {
  type: 'fixed' | 'percent';
  value: number;
}

export interface StockIntake {
  intakeId: string;
  status: IntakeStatus;
  supplierId?: string | null;
  supplierName?: string | null;
  invoiceNo?: string | null;
  /** The supplier's delivery note / GRN number. */
  deliveryNoteNo?: string | null;
  receivedBy?: string | null;
  /** Set when posted. */
  postedVia?: PostedVia | null;
  attachments?: IntakeAttachment[];
  receivedAt: string;
  notes?: string | null;
  lines: IntakeLine[];
  charges: IntakeCharge[];
  discount?: IntakeDiscount | null;
  subtotal: number;
  chargesTotal: number;
  discountTotal: number;
  total: number;
  createdAt?: string;
  updatedAt?: string;
  postedAt?: string;
  postedBy?: string;
  voidedAt?: string;
  voidedBy?: string;
  voidReason?: string;
  /** Set by the legacy importer; these carry no ledger events. */
  legacy?: boolean;
}

export interface IntakeInput {
  /** Client-chosen id; makes receive-now safe to retry. */
  intakeId?: string;
  supplierId?: string | null;
  invoiceNo?: string | null;
  deliveryNoteNo?: string | null;
  receivedBy?: string | null;
  receivedAt?: string;
  notes?: string | null;
  lines: IntakeLine[];
  charges?: IntakeCharge[];
  discount?: IntakeDiscount | null;
}

export interface IntakeListFilters {
  status?: IntakeStatus;
  supplierId?: string;
  from?: string;
  to?: string;
  limit?: number;
  lastKey?: string;
  /** Prefix search over invoice no, delivery note no and supplier name. */
  q?: string;
}

/** What the server did to the ledger, returned by post and void. */
export interface IntakeEventSummary {
  syncedCount: number;
  skippedCount: number;
  failedCount: number;
  results?: {eventId: string; result: string; reason?: string}[];
}

export interface IntakePostResult {
  intake: StockIntake;
  events: IntakeEventSummary;
  targets?: {
    products?: {productId: string; onHand: number; soldOut: boolean}[];
    variants?: {productId: string; variantId: string; onHand: number; soldOut: boolean}[];
    ingredients?: {stockId: string; onHand: number}[];
    dishes?: {productId: string; onHand: number; soldOut: boolean; limitingStockId?: string}[];
  };
  alreadyPosted?: boolean;
  alreadyVoid?: boolean;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON * Math.abs(n)) * 100) / 100;

/**
 * A preview of the totals, for the editor only.
 *
 * Mirrors the server's rule — discount comes off the goods, charges are added
 * after — so the figure does not jump when the document saves. If the two
 * ever disagree, the SERVER is right: it is what gets stored.
 */
export function estimateTotals(input: {
  lines?: IntakeLine[];
  charges?: IntakeCharge[];
  discount?: IntakeDiscount | null;
}): {subtotal: number; chargesTotal: number; discountTotal: number; total: number} {
  const n = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const subtotal = round2(
    (input.lines ?? []).reduce((sum, l) => sum + round2(n(l.qty) * n(l.unitCost)), 0),
  );
  const chargesTotal = round2((input.charges ?? []).reduce((sum, c) => sum + n(c.amount), 0));

  let discountTotal = 0;
  if (input.discount && n(input.discount.value) > 0) {
    discountTotal = input.discount.type === 'percent'
      ? round2(subtotal * (n(input.discount.value) / 100))
      : round2(n(input.discount.value));
  }
  discountTotal = Math.min(discountTotal, subtotal);

  return {subtotal, chargesTotal, discountTotal, total: round2(subtotal - discountTotal + chargesTotal)};
}

/** Why a file cannot be attached, or null. Mirrors the server's checks. */
export function attachmentProblem(file: {type: string; size: number}): string | null {
  if (!ATTACHMENT_TYPES.includes(file.type)) return 'Only photos (JPEG, PNG, WebP, HEIC) and PDFs can be attached';
  if (!(file.size > 0)) return 'That file is empty';
  if (file.size > MAX_ATTACHMENT_BYTES) return 'Attachments are limited to 10 MB';
  return null;
}

/** A fresh client-side intake id, for a retry-safe receive-now. */
export function newIntakeId(): string {
  const g: any = globalThis as any;
  const raw = g.crypto?.randomUUID
    ? g.crypto.randomUUID().replace(/-/g, '')
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  return `in_${raw.slice(0, 16)}`;
}

const StockIntakeRepository = {
  async list(shopId: string, filters: IntakeListFilters = {}): Promise<{
    intakes: StockIntake[];
    lastKey?: string;
  }> {
    const params = [`merchantId=${encodeURIComponent(shopId)}`];
    if (filters.status) params.push(`status=${filters.status}`);
    if (filters.supplierId) params.push(`supplierId=${encodeURIComponent(filters.supplierId)}`);
    if (filters.from) params.push(`from=${encodeURIComponent(filters.from)}`);
    if (filters.to) params.push(`to=${encodeURIComponent(filters.to)}`);
    if (filters.limit) params.push(`limit=${filters.limit}`);
    if (filters.lastKey) params.push(`lastKey=${filters.lastKey}`);
    if (filters.q) params.push(`q=${encodeURIComponent(filters.q)}`);

    const res = await posApiRequest(`/stock/intakes?${params.join('&')}`);
    return {intakes: res?.intakes ?? [], lastKey: res?.lastKey};
  },

  async get(shopId: string, intakeId: string): Promise<StockIntake> {
    const res = await posApiRequest(
      `/stock/intakes/${encodeURIComponent(intakeId)}?merchantId=${encodeURIComponent(shopId)}`,
    );
    return res?.intake;
  },

  async create(shopId: string, input: IntakeInput): Promise<StockIntake> {
    const res = await posApiRequest('/stock/intakes', {
      method: 'POST',
      body: JSON.stringify({merchantId: shopId, ...input}),
    });
    return res?.intake;
  },

  /** Drafts only. The server answers 409 for anything else. */
  async update(shopId: string, intakeId: string, input: IntakeInput): Promise<StockIntake> {
    const res = await posApiRequest(
      `/stock/intakes/${encodeURIComponent(intakeId)}?merchantId=${encodeURIComponent(shopId)}`,
      {method: 'PUT', body: JSON.stringify({merchantId: shopId, ...input})},
    );
    return res?.intake;
  },

  async remove(shopId: string, intakeId: string): Promise<void> {
    await posApiRequest(
      `/stock/intakes/${encodeURIComponent(intakeId)}?merchantId=${encodeURIComponent(shopId)}`,
      {method: 'DELETE'},
    );
  },

  /**
   * Append the purchase events and freeze the document.
   *
   * Idempotent: ids are `purchase:{intakeId}:{lineId}`, so a retried click
   * returns the same document with every event `skipped: duplicate`.
   */
  async post(shopId: string, intakeId: string, postedBy?: string): Promise<IntakePostResult> {
    return posApiRequest(
      `/stock/intakes/${encodeURIComponent(intakeId)}/post?merchantId=${encodeURIComponent(shopId)}`,
      {method: 'POST', body: JSON.stringify({postedBy})},
    );
  },

  /**
   * Create and post in one step — "Receive now". The same document and the
   * same events as draft-then-post. Pass a client-made `intakeId` so a retry
   * after a lost response cannot receive the goods twice.
   */
  async receiveNow(shopId: string, input: IntakeInput & {intakeId: string}): Promise<IntakePostResult> {
    return posApiRequest('/stock/intakes/receive', {
      method: 'POST',
      body: JSON.stringify({merchantId: shopId, ...input}),
    });
  },

  /** The lines of this supplier's last posted delivery, for prefill. */
  async last(shopId: string, supplierId: string): Promise<IntakeLine[]> {
    const res = await posApiRequest(
      `/stock/intakes/last?merchantId=${encodeURIComponent(shopId)}` +
        `&supplierId=${encodeURIComponent(supplierId)}`,
    );
    return res?.lines ?? [];
  },

  /** Text search over invoice no, delivery note no and supplier name. */
  async search(
    shopId: string,
    q: string,
    filters: IntakeListFilters = {},
  ): Promise<{intakes: StockIntake[]; lastKey?: string}> {
    return StockIntakeRepository.list(shopId, {...filters, q});
  },

  /** Record a file already PUT to S3. The server checks it is really there. */
  async confirmAttachment(
    shopId: string,
    intakeId: string,
    attachmentId: string,
    key: string,
    fileName: string,
  ): Promise<{attachment: IntakeAttachment; intake: StockIntake}> {
    return posApiRequest(
      `/stock/intakes/${encodeURIComponent(intakeId)}/attachments/` +
        `${encodeURIComponent(attachmentId)}/confirm?merchantId=${encodeURIComponent(shopId)}`,
      {method: 'PUT', body: JSON.stringify({merchantId: shopId, key, fileName})},
    );
  },

  /**
   * Attach one file: ask for a presigned PUT, upload straight to S3, then
   * confirm. Rejects a wrong type or size before any request is made.
   */
  async requestAttachmentUpload(
    shopId: string,
    intakeId: string,
    file: File,
  ): Promise<{attachment: IntakeAttachment; intake: StockIntake}> {
    const problem = attachmentProblem(file);
    if (problem) throw new Error(problem);

    const req = await posApiRequest(
      `/stock/intakes/${encodeURIComponent(intakeId)}/attachments?merchantId=${encodeURIComponent(shopId)}`,
      {
        method: 'POST',
        body: JSON.stringify({
          merchantId: shopId,
          fileName: file.name,
          contentType: file.type,
          size: file.size,
        }),
      },
    );

    // Straight to S3: no API key, and the Content-Type must be the one signed.
    const put = await fetch(req.uploadUrl, {
      method: 'PUT',
      headers: {'Content-Type': file.type},
      body: file,
    });
    if (!put.ok) throw new Error(`Upload failed (${put.status})`);

    return StockIntakeRepository.confirmAttachment(
      shopId,
      intakeId,
      req.attachmentId,
      req.key,
      file.name,
    );
  },

  /** Drafts only; the server answers 409 for a posted document. */
  async deleteAttachment(shopId: string, intakeId: string, attachmentId: string): Promise<StockIntake> {
    const res = await posApiRequest(
      `/stock/intakes/${encodeURIComponent(intakeId)}/attachments/` +
        `${encodeURIComponent(attachmentId)}?merchantId=${encodeURIComponent(shopId)}`,
      {method: 'DELETE'},
    );
    return res?.intake;
  },

  /** Append the reversals. Also idempotent. */
  async voidIntake(
    shopId: string,
    intakeId: string,
    voidReason: string,
    voidedBy?: string,
  ): Promise<IntakePostResult> {
    return posApiRequest(
      `/stock/intakes/${encodeURIComponent(intakeId)}/void?merchantId=${encodeURIComponent(shopId)}`,
      {method: 'POST', body: JSON.stringify({voidReason, voidedBy})},
    );
  },
};

export default StockIntakeRepository;
